import { forwardRef } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Select, SelectProps, Text } from '@hhgtech/hhg-components/mantine'
import { Search } from '@hhgtech/icons/core'
import {
  CSSObject,
  Flex,
  LoadingOverlay,
  rem,
  ScrollArea,
  Stack,
} from '@mantine/core'
import { useIntl } from 'react-intl'

import { ClinicSearchItem } from './ClinicSearchItem'

export type ClinicLocatorSidebarSearchProps = SelectProps & {
  loading?: boolean
  onClickLeftIcon?: () => void
}
export const ClinicLocatorSidebarSearch = forwardRef<
  HTMLInputElement,
  ClinicLocatorSidebarSearchProps
>((props, ref) => {
  const {
    loading,
    data,
    placeholder,
    onClickLeftIcon,
    value,
    icon = <Search color={theme.colors.gray300} onClick={onClickLeftIcon} />,
    ...restProps
  } = props
  const { formatMessage: f } = useIntl()

  const searchPlaceholder = f({
    id: 'clinicLocator.inputPlaceholder',
    defaultMessage: 'Search by hospital name',
  })

  const nothingFound = (
    <Stack
      align="center"
      sx={{ position: 'relative', padding: rem(40), width: '100%' }}
    >
      <LoadingOverlay
        visible={!!loading}
        sx={() => ({ borderRadius: rem(20), border: 0 })}
      />
      <Flex
        align="center"
        justify="center"
        sx={{
          background: 'white',
          borderRadius: '50%',
          width: rem(120),
          height: rem(120),
          img: {
            width: rem(80),
          },
        }}
      >
        <img
          src={CommonGAssets.getAssetPath('hospital.png')}
          alt="Not found"
          loading="lazy"
        />
      </Flex>
      <Text size="p4">
        {f({
          id: 'clinicLocator.notFound',
          defaultMessage: 'No result found. Try another keyword!',
        })}
      </Text>
    </Stack>
  )

  return (
    <Select
      ref={ref}
      size="lg"
      clearable
      keepSearch
      searchable
      withinPortal
      limit={20}
      icon={icon}
      disabled={loading}
      data={data}
      inputMode="text"
      value={(value || '') as string}
      placeholder={placeholder || searchPlaceholder}
      filter={() => true}
      itemComponent={ClinicSearchItem}
      rightSection={null}
      dropdownComponent={({ children }: any) => {
        return (
          <ScrollArea scrollbarSize={8} className="w-full">
            {children}
            <LoadingOverlay
              visible={!!loading}
              sx={() => ({ borderRadius: rem(20), border: 0 })}
            />
          </ScrollArea>
        )
      }}
      nothingFound={nothingFound}
      styles={() => {
        const dropdown: CSSObject = {
          border: 0,
          padding: 0,
          borderRadius: rem(20),
        }
        if (!value || (!data.length && !loading)) {
          dropdown.display = 'none'
        }

        return {
          input: {
            borderRadius: rem(8),
            borderColor: theme.colors.gray200,
            fontSize: rem(16),
            lineHeight: 1.5,
            '&[data-with-icon]': {
              paddingLeft: rem(48),
            },
          },
          dropdown,
          item: {
            borderRadius: 0,
            borderTop: `1px solid ${theme.colors.neutral100}`,
            '&:first-of-type': {
              borderTop: 0,
            },
          },
        }
      }}
      {...restProps}
    />
  )
})
